/**
 * Analysis API Layer
 * 
 * Provides stable read/write contracts for analysis data while shielding
 * column drift between telemetry_ai and ai_meta columns.
 * 
 * All reads go through v_analysis_meta view for consistency.
 * Writes use feature-flagged approach for gradual migration.
 */

import { supabase } from '@/integrations/supabase/client';

export interface AnalysisMetadata {
  model?: string;
  analysis_tier?: string;
  focus_area?: string;
  skill_level?: string;
  latency_ms?: number;
  tokens_in?: number;
  tokens_out?: number;
  est_cost_usd?: number;
  runId?: string;
  created_at?: string;
  [key: string]: any; // Allow additional metadata
}

export interface AnalysisRecord {
  id: string;
  user_id: string;
  video_asset_id: string;
  video_id?: string;
  policy: string;
  clip_ms: number;
  width: number;
  height: number;
  mode: string;
  status: string;
  processed_frames: number;
  drawn_frames: number;
  detect_cadence: number;
  first_result_at_ms?: number;
  error?: string;
  storage_path?: string;
  metrics?: any;
  cost?: any;
  created_at: string;
  completed_at?: string;
  metrics_present?: boolean;
  // AI analysis fields
  ai_mediapipe_v1?: any;
  ai_mediapipe_v1_enriched?: any;
  meta?: AnalysisMetadata; // Unified metadata from view
  meta_source?: 'telemetry_ai' | 'ai_meta' | null;
  meta_updated_at?: string;
}

export interface WriteAnalysisParams {
  runId: string;
  aiAnalysis?: any;
  enrichedAnalysis?: any;
  metadata?: AnalysisMetadata;
  useAiMetaColumn?: boolean; // Feature flag for legacy column
}

/**
 * Read analysis data using the unified view
 */
export class AnalysisAPI {
  
  /**
   * Get analysis record by ID using unified view
   */
  static async getAnalysisById(runId: string): Promise<AnalysisRecord | null> {
    const { data, error } = await supabase
      .from('v_analysis_meta')
      .select('*')
      .eq('id', runId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      throw new Error(`Failed to get analysis: ${error.message}`);
    }

    return data;
  }

  /**
   * List analysis records for a video asset using unified view
   */
  static async listAnalysisForAsset(
    videoAssetId: string, 
    limit = 5
  ): Promise<AnalysisRecord[]> {
    const { data, error } = await supabase
      .from('v_analysis_meta')
      .select('*')
      .eq('video_asset_id', videoAssetId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to list analysis: ${error.message}`);
    }

    return data || [];
  }

  /**
   * List analysis records for a user using unified view
   */
  static async listAnalysisForUser(
    userId: string,
    limit = 20,
    offset = 0
  ): Promise<AnalysisRecord[]> {
    const { data, error } = await supabase
      .from('v_analysis_meta')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to list user analysis: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Write analysis data with feature-flagged column selection
   */
  static async writeAnalysis(params: WriteAnalysisParams): Promise<void> {
    const { runId, aiAnalysis, enrichedAnalysis, metadata, useAiMetaColumn } = params;

    // Prepare update object
    const updates: any = {};

    if (aiAnalysis !== undefined) {
      updates.ai_mediapipe_v1 = aiAnalysis;
    }

    if (enrichedAnalysis !== undefined) {
      updates.ai_mediapipe_v1_enriched = enrichedAnalysis;
    }

    if (metadata !== undefined) {
      // Feature flag determines which column to write to
      if (useAiMetaColumn) {
        updates.ai_meta = metadata;
      } else {
        updates.telemetry_ai = metadata;
      }
    }

    if (Object.keys(updates).length === 0) {
      return; // Nothing to update
    }

    const { error } = await supabase
      .from('swing_analysis_runs')
      .update(updates)
      .eq('id', runId);

    if (error) {
      throw new Error(`Failed to write analysis: ${error.message}`);
    }
  }

  /**
   * Get metadata using the helper function (with proper typing)
   */
  static async getAnalysisMetadata(runId: string): Promise<{
    meta: AnalysisMetadata | null;
    meta_source: 'telemetry_ai' | 'ai_meta' | null;
    meta_updated_at: string | null;
  }> {
    const { data, error } = await supabase
      .rpc('get_analysis_meta', { p_run_id: runId });

    if (error) {
      throw new Error(`Failed to get analysis metadata: ${error.message}`);
    }

    const result = data?.[0];
    return {
      meta: result?.meta || null,
      meta_source: result?.meta_source || null,
      meta_updated_at: result?.meta_updated_at || null
    };
  }

  /**
   * Get column usage statistics for monitoring
   */
  static async getColumnStats(): Promise<{
    total_runs: number;
    telemetry_ai_count: number;
    ai_meta_count: number;
    both_columns_count: number;
    no_meta_count: number;
    telemetry_ai_percentage: number;
    ai_meta_percentage: number;
  }> {
    const { data, error } = await supabase
      .from('v_analysis_meta_stats')
      .select('*')
      .single();

    if (error) {
      throw new Error(`Failed to get column stats: ${error.message}`);
    }

    return data;
  }

  /**
   * Check if a run has any metadata (from either column)
   */
  static async hasMetadata(runId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('v_analysis_meta')
      .select('meta')
      .eq('id', runId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return false; // Not found
      }
      throw new Error(`Failed to check metadata: ${error.message}`);
    }

    return data?.meta !== null;
  }

  /**
   * Migrate metadata from ai_meta to telemetry_ai for a specific run
   */
  static async migrateRunMetadata(runId: string): Promise<boolean> {
    // First, check if migration is needed
    const { data: current, error: fetchError } = await supabase
      .from('swing_analysis_runs')
      .select('ai_meta, telemetry_ai')
      .eq('id', runId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch run for migration: ${fetchError.message}`);
    }

    // Skip if no ai_meta or telemetry_ai already exists
    if (!current.ai_meta || current.telemetry_ai) {
      return false;
    }

    // Migrate ai_meta to telemetry_ai
    const { error: updateError } = await supabase
      .from('swing_analysis_runs')
      .update({ 
        telemetry_ai: current.ai_meta,
        // Optionally clear ai_meta after migration
        // ai_meta: null 
      })
      .eq('id', runId);

    if (updateError) {
      throw new Error(`Failed to migrate run metadata: ${updateError.message}`);
    }

    return true;
  }
}

// Export singleton instance for convenience
export const analysisAPI = new AnalysisAPI();
